import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { parseISO, startOfDay } from "date-fns";
import { Alert, CircularProgress } from "@mui/material";

import {
  RecurringSummaryCountsProps_T,
  RecurringSummaryCountsPayload_T,
  RecurringSummaryCountsResponse_T,
} from "../../../types/ScheduleForm";

import useApi from "../../../hooks/useGaneshApi";
import pluralize from "pluralize";

function hasNoRecurringDeliveries(dataObj: Record<string, number>): boolean {
  return Object.values(dataObj).every((value) => value === 0);
}

function generateSummary(
  data: RecurringSummaryCountsResponse_T | null,
  selectedItems: number[],
  startDate: string,
  endDate: string,
  isRemoving: boolean
): string {
  if (!data) return "";

  const countSelectedItemsPluralized = pluralize(
    "delivery",
    selectedItems.length
  );
  const countCustomersPluralized = pluralize("customer", data.count_customers);
  const countDeliveriesPluralized = pluralize(
    "delivery",
    data.count_deliveries
  );
  const countFulfillmentDatesPluralized = pluralize(
    "delivery date",
    data.count_fulfillment_dates
  );
  const countDeliveryEventsPluralized = pluralize(
    "delivery event",
    data.count_delivery_events
  );
  const countVolumeUpgradesPluralized = pluralize(
    "volume upgrade",
    data.count_volume_upgrades
  );

  const hasNoRecurring = data ? hasNoRecurringDeliveries(data) : false;

  // handle remove messaging
  if (isRemoving) {
    if (hasNoRecurring) {
      return `The current ${pluralize(
        "selection",
        selectedItems.length
      )} ${pluralize("has", selectedItems.length)} no recurring deliveries.`;
    }

    return `You are deleting ${selectedItems.length} ${countSelectedItemsPluralized} with ${data.count_delivery_events} ${countDeliveryEventsPluralized} and ${data.count_volume_upgrades} ${countVolumeUpgradesPluralized}.`;
  }

  // handle add/edit messaging
  if (hasNoRecurring) {
    return `You have selected ${selectedItems.length} ${countSelectedItemsPluralized} with no existing delivery events in the selected date range: ${startDate} to ${endDate}.`;
  }

  return `You have ${data.count_deliveries} ${countDeliveriesPluralized},
          across ${data.count_customers} ${countCustomersPluralized},
          with ${data.count_fulfillment_dates} ${countFulfillmentDatesPluralized}
          on schedule in the selected date range, for a total of
          ${data.count_delivery_events} ${countDeliveryEventsPluralized}
          and ${data.count_volume_upgrades} ${countVolumeUpgradesPluralized}
          which will be overwritten.`;
}

const RecurringSummaryCounts: React.FC<RecurringSummaryCountsProps_T> = ({
  selectedItems,
  startDate,
  endDate,
  isRemoving = false,
  setIsValidRemoval,
}) => {
  const { apiRequest } = useApi();

  const [data, setData] = useState<RecurringSummaryCountsResponse_T | null>(
    null
  );

  const { mutate: postSummaryCounts, isPending } = useMutation({
    mutationFn: async (payload: RecurringSummaryCountsPayload_T) => {
      const response = await apiRequest<RecurringSummaryCountsResponse_T>(
        "/schedule/get-shipping-summary-counts",
        {
          method: "POST",
          body: payload,
        }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      setData(data.data);
      // note: inverting the check
      const isValidRemoval = data.data
        ? !hasNoRecurringDeliveries(data.data)
        : false;
      setIsValidRemoval?.(isValidRemoval);
    },
    onError: (error) => {
      console.error(error);
      setData(null);
    },
  });

  useEffect(() => {
    if (startDate !== "" && selectedItems.length > 0) {
      setIsValidRemoval?.(false);

      const payload: RecurringSummaryCountsPayload_T = {
        dateStart: startOfDay(parseISO(startDate)).toISOString(),
        delivery_ids: selectedItems,
      };

      if (isRemoving) {
        postSummaryCounts(payload);
      } else {
        if (endDate && endDate !== "") {
          payload.dateEnd = startOfDay(parseISO(endDate)).toISOString();
        }
        postSummaryCounts(payload);
      }
    } else {
      setData(null);
    }
  }, [
    startDate,
    endDate,
    selectedItems.join(","),
    postSummaryCounts,
    isRemoving,
    setIsValidRemoval,
  ]);

  return (
    <>
      <Alert severity="info" icon={false}>
        {isPending && (
          <CircularProgress
            size={20}
            sx={{
              verticalAlign: "middle",
            }}
          />
        )}
        {!isPending &&
          !data &&
          !isRemoving &&
          "Enter a valid date range and select deliveries to show summary."}
        {!isPending &&
          !data &&
          isRemoving &&
          "Select one or more deliveries to see summary."}
        {!isPending &&
          data &&
          generateSummary(
            data,
            selectedItems,
            startDate,
            endDate || "",
            isRemoving
          )}
      </Alert>
    </>
  );
};

export default RecurringSummaryCounts;
